/**
 * A árvore "Originais e cópias de trabalho" da Custódia (E126.F3; separada do passo na E134.F2).
 *
 * Cada original mostra o instante, o hash e a situação da conferência mais recente: "preservado"
 * só quando a conferência mostrou que o arquivo guardado reproduz o hash do recebimento (GUS-159,
 * ADR-0090). Cada derivado mostra o vínculo, a transformação que o gerou e o hash dele — desde a
 * E162.F2.T03, atrás de "Ver cópias de trabalho", para o original ficar à vista. O derivado
 * sem origem viva fica à parte (F-E126.F3-01), e o arquivo guardado sem exame fica pendurado no
 * pacote de onde saiu, ou solto.
 */
import { Badge } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import {
  hashCurto,
  naoExaminadosDoRecibo,
  type DerivadoSemOrigemData,
  type DocumentoDoMaterialData,
  type NaoExaminadoNoReciboData,
  type OriginalRecebidoData,
  type ReciboDaCustodiaData,
} from '@/lib/abertura_do_caso';
import { formatarDataHora } from '@/lib/trilha_format';

import { CartaoDaAbertura } from './PecasDaAbertura';
import { LinhaNaoExaminadoDoRecibo } from './ReciboDaCustodia';
import { SeloDaSituacao, textoDaSituacao } from './SituacaoDaCustodia';

const t = ptBR.abertura.custodia;

function HashDoArquivo({ sha256 }: { sha256: string | null | undefined }): JSX.Element | null {
  const hash = hashCurto(sha256 ?? null);
  if (hash === null) return null;
  return (
    <>
      {' · '}
      <span className="font-mono" title={sha256 ?? undefined}>
        {hash}
      </span>
    </>
  );
}

function textoDaTransformacao(derivado: DocumentoDoMaterialData): string {
  if (derivado.transformacao === null || derivado.transformacao === undefined) {
    return t.derivadoLinha;
  }
  const paginas = (derivado.paginas ?? []).join(', ');
  return t.transformacao[derivado.transformacao].replace('{paginas}', paginas);
}

function ListaDeDerivados({
  derivados,
}: {
  derivados: DocumentoDoMaterialData[];
}): JSX.Element | null {
  if (derivados.length === 0) return null;
  // E162.F2.T03 (GUS-135): os originais à vista; as cópias de trabalho de cada um por expansão.
  return (
    <details className="ml-sc-4" data-testid="custodia-copias">
      <summary className="cursor-pointer py-1 text-caption text-accent-700">
        {t.verCopias.replace('{n}', String(derivados.length))}
      </summary>
      <ul className="border-l border-border pl-sc-4">
        {derivados.map((derivado) => (
          <li
            key={derivado.documento_id}
            data-testid="custodia-derivado"
            className="flex flex-wrap items-center justify-between gap-sc-2 py-1"
          >
            <span className="min-w-0">
              <span className="block truncate text-body-sm text-fg">{derivado.nome}</span>
              <span className="text-caption text-fg-muted">
                {textoDaTransformacao(derivado)}
                <HashDoArquivo sha256={derivado.sha256} />
              </span>
            </span>
            <span className="flex gap-sc-2">
              <SeloDaSituacao conferencia={derivado.conferencia} />
              <Badge variant="info">{t.seloDerivado}</Badge>
            </span>
          </li>
        ))}
      </ul>
    </details>
  );
}

function SeloDoOriginal({ original }: { original: OriginalRecebidoData }): JSX.Element | null {
  if (original.conferencia) return <SeloDaSituacao conferencia={original.conferencia} />;
  const temHash = original.sha256 !== null;
  return (
    <Badge variant={temHash ? 'success' : 'warning'}>{temHash ? t.seloHash : t.seloSemHash}</Badge>
  );
}

function LinhaDoOriginal({
  original,
  naoExaminados,
}: {
  original: OriginalRecebidoData;
  naoExaminados: NaoExaminadoNoReciboData[];
}): JSX.Element {
  return (
    <li data-testid="custodia-original">
      <div className="flex flex-wrap items-center justify-between gap-sc-2 py-sc-2">
        <div className="min-w-0">
          <p className="truncate text-body-sm font-semibold text-fg">{original.nome}</p>
          <p className="text-caption text-fg-muted">
            {t.originalLinha} · {textoDaSituacao(original.conferencia)} ·{' '}
            {formatarDataHora(original.recebido_em)}
            <HashDoArquivo sha256={original.sha256} />
          </p>
        </div>
        <SeloDoOriginal original={original} />
      </div>
      <ListaDeDerivados derivados={original.derivados} />
      {naoExaminados.length > 0 ? (
        <ul className="ml-sc-4 border-l border-border pl-sc-4">
          {naoExaminados.map((item) => (
            <LinhaNaoExaminadoDoRecibo key={item.item_id} item={item} />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

function LinhaSemOrigem({ derivado }: { derivado: DerivadoSemOrigemData }): JSX.Element {
  return (
    <li data-testid="custodia-sem-origem">
      <div className="flex flex-wrap items-center justify-between gap-sc-2 py-sc-2">
        <div className="min-w-0">
          <p className="truncate text-body-sm font-semibold text-fg">{derivado.nome}</p>
          <p className="text-caption text-fg-muted">
            {t.semOrigemLinha} · {formatarDataHora(derivado.gerado_em)}
            <HashDoArquivo sha256={derivado.sha256} />
          </p>
          <p className="break-all text-caption text-fg-muted">
            {t.semOrigemReferencia}: <span className="font-mono">{derivado.origem_ausente_id}</span>
          </p>
        </div>
        <span className="flex gap-sc-2">
          <SeloDaSituacao conferencia={derivado.conferencia} />
          <Badge variant="warning">{t.seloSemOrigem}</Badge>
        </span>
      </div>
      <ListaDeDerivados derivados={derivado.derivados} />
    </li>
  );
}

/**
 * `compacto` (E163.F2, A67-11): sob "Ver recibo" a árvore vem logo depois do recibo, sem cartão
 * próprio, como no HTML 6.7; fora dele, o cartão "Originais e cópias de trabalho".
 */
export function ArvoreDaCustodia({
  recibo,
  compacto = false,
}: {
  recibo: ReciboDaCustodiaData;
  compacto?: boolean;
}): JSX.Element {
  const naoExaminados = naoExaminadosDoRecibo(recibo);
  // O que saiu de um pacote fica pendurado nele; o resto, ou o do pacote que não está mais no
  // caso, aparece solto na árvore.
  const pacotes = new Set(recibo.originais.map((o) => o.documento_id));
  const soltos = naoExaminados.filter(
    (n) => n.pacote_document_id === null || !pacotes.has(n.pacote_document_id),
  );
  const lista = (
    <ul
      className="divide-y divide-border"
      {...(compacto ? { 'data-testid': 'custodia-arvore' } : {})}
    >
      {recibo.originais.map((original) => (
        <LinhaDoOriginal
          key={original.documento_id}
          original={original}
          naoExaminados={naoExaminados.filter(
            (n) => n.pacote_document_id === original.documento_id,
          )}
        />
      ))}
      {recibo.derivados_sem_origem.map((derivado) => (
        <LinhaSemOrigem key={derivado.documento_id} derivado={derivado} />
      ))}
      {soltos.map((item) => (
        <LinhaNaoExaminadoDoRecibo key={item.item_id} item={item} />
      ))}
    </ul>
  );
  if (compacto) return lista;
  return (
    <CartaoDaAbertura titulo={t.arvoreTitulo} testId="custodia-arvore">
      {lista}
    </CartaoDaAbertura>
  );
}
